/**
 * Kriegsakademie — the lessons. Each step is an instructor's note with an optional task whose
 * completion is read from the stores (never from DOM events), so the player can do things any
 * way the game allows: hotkeys, buttons or the map. Conditions compare the live snapshot with a
 * baseline captured when the step opened, so earlier actions never complete a later lesson.
 *
 * Pure data + pure functions; unit-tested in test/tutorial.test.ts.
 */
import type { GameEvent, HexGrid, HexId, Order, PlayerView, Posture, TechId, UnitId } from '@friction/engine';
import { getLocale, type Locale, type MsgKey, tr } from '../i18n';

/** HUD elements a lesson can point at (`data-tut` attribute on the element). */
export type TutorialAnchor =
  | 'minimap'
  | 'selection'
  | 'orders'
  | 'speed'
  | 'teletype'
  | 'intel'
  | 'production'
  | 'research'
  | 'posture'
  | 'hhour'
  | 'readout'
  | 'vp';

/** Camera centre and zoom, from the renderer's bus `camera` reports. */
export interface CameraPos {
  x: number;
  y: number;
  /** Visible world width — pan distances are judged relative to it. */
  width: number;
  zoom: number;
}

export interface TutorialSnapshot {
  view: PlayerView;
  grid: HexGrid;
  selection: readonly UnitId[];
  inspectedContact: UnitId | null;
  paused: boolean;
  camera: CameraPos | null;
  log: readonly GameEvent[];
}

/** What the world looked like when the step opened. */
export interface StepBaseline {
  tick: number;
  camera: CameraPos | null;
  /** Highest own order id known at the time; newer orders have higher ids. */
  maxOrderId: number;
  postures: Record<UnitId, Posture>;
  productionIds: number[];
  research: TechId | null;
}

/** The lessons, in order. Their words live in the catalogue: `tut.<id>.title|body|task`. */
export type LessonId =
  | 'camera'
  | 'select'
  | 'card'
  | 'hq'
  | 'order'
  | 'clock'
  | 'intel'
  | 'attack'
  | 'hhour'
  | 'posture'
  | 'production'
  | 'research'
  | 'supply'
  | 'victory';

export interface TutorialStep {
  id: LessonId;
  highlight?: TutorialAnchor;
  /** Hex to show when the step opens (and on "Show me"). */
  focus?: (s: TutorialSnapshot) => HexId | null;
  /** Completion condition; absent on reading steps (Next to continue). A task line goes with it. */
  done?: (s: TutorialSnapshot, b: StepBaseline) => boolean;
}

type TaskKey = Extract<MsgKey, `tut.${string}.task`>;

/** A lesson's words: title, paragraphs (`[Key]` = keycap, `*text*` = emphasis) and task line. */
export function lessonText(
  step: TutorialStep,
  locale: Locale = getLocale(),
): { title: string; body: string[]; task: string | null } {
  const t = tr(locale);
  return {
    title: t(`tut.${step.id}.title`),
    body: t(`tut.${step.id}.body`).split('\n\n'),
    task: step.done ? t(`tut.${step.id}.task` as TaskKey) : null,
  };
}

// ---- helpers ------------------------------------------------------------------------------------

const ownUnits = (v: PlayerView) => v.units.filter((u) => u.faction === v.faction);

/** Every own order the view knows about: executing, awaiting H-hour, on the wire or on a horse. */
export function ownOrders(v: PlayerView): Order[] {
  const out: Order[] = [];
  for (const u of ownUnits(v)) {
    if (u.order) out.push(u.order);
    if (u.queuedOrder) out.push(u.queuedOrder);
  }
  for (const c of v.couriers) out.push(c.order);
  for (const d of v.dispatches) out.push(d.order);
  return out;
}

/** Orders issued after the baseline was taken. */
export const newOrders = (v: PlayerView, b: StepBaseline): Order[] =>
  ownOrders(v).filter((o) => o.id > b.maxOrderId);

export function captureBaseline(s: TutorialSnapshot): StepBaseline {
  const postures: Record<UnitId, Posture> = {};
  for (const u of ownUnits(s.view)) postures[u.id] = u.posture;
  return {
    tick: s.view.tick,
    camera: s.camera,
    maxOrderId: ownOrders(s.view).reduce((m, o) => Math.max(m, o.id), 0),
    postures,
    productionIds: s.view.me.production.map((p) => p.id),
    research: s.view.me.research.current,
  };
}

const hq = (v: PlayerView) => ownUnits(v).find((u) => u.type === 'hq');

/** Own non-HQ division closest to the enemy (a front-line unit), else any. */
function frontDivision(s: TutorialSnapshot) {
  const divs = ownUnits(s.view).filter((u) => u.type !== 'hq');
  const seen = s.view.contacts.filter((c) => c.visibleNow);
  if (!seen.length) return divs[0] ?? null;
  let best = divs[0] ?? null;
  let bestD = Number.POSITIVE_INFINITY;
  for (const u of divs) {
    if (u.type === 'artillery') continue;
    for (const c of seen) {
      const d = s.grid.distance(u.hex, c.hex);
      if (d < bestD) {
        bestD = d;
        best = u;
      }
    }
  }
  return best;
}

/** Enemy contact nearest to our divisions (observed ones first). */
function nearestContact(s: TutorialSnapshot): HexId | null {
  const divs = ownUnits(s.view).filter((u) => u.type !== 'hq');
  const pool = s.view.contacts.some((c) => c.visibleNow)
    ? s.view.contacts.filter((c) => c.visibleNow)
    : s.view.contacts;
  let best: HexId | null = null;
  let bestD = Number.POSITIVE_INFINITY;
  for (const c of pool)
    for (const u of divs) {
      const d = s.grid.distance(u.hex, c.hex);
      if (d < bestD) {
        bestD = d;
        best = c.hex;
      }
    }
  return best;
}

// ---- the syllabus -------------------------------------------------------------------------------

export const TUTORIAL_STEPS: readonly TutorialStep[] = [
  {
    id: 'camera',
    highlight: 'minimap',
    done: (s, b) => {
      if (!s.camera || !b.camera) return false;
      const moved = Math.hypot(s.camera.x - b.camera.x, s.camera.y - b.camera.y) > b.camera.width * 0.2;
      const zoomed = Math.abs(Math.log(s.camera.zoom / b.camera.zoom)) > 0.1;
      return moved && zoomed;
    },
  },
  {
    id: 'select',
    focus: (s) => frontDivision(s)?.hex ?? null,
    done: (s) =>
      s.selection.some((id) =>
        s.view.units.some((u) => u.id === id && u.faction === s.view.faction && u.type !== 'hq'),
      ),
  },
  {
    id: 'card',
    highlight: 'selection',
  },
  {
    id: 'hq',
    focus: (s) => hq(s.view)?.hex ?? null,
    done: (s) =>
      s.selection.some((id) =>
        s.view.units.some((u) => u.id === id && u.faction === s.view.faction && u.type === 'hq'),
      ),
  },
  {
    id: 'order',
    highlight: 'orders',
    done: (s, b) =>
      newOrders(s.view, b).some((o) => o.kind === 'move' || o.kind === 'attack' || o.kind === 'retreat'),
  },
  {
    id: 'clock',
    highlight: 'speed',
    done: (s, b) =>
      s.log.some((e) => e.kind === 'orderDelivered' && e.tick > b.tick) || s.view.tick >= b.tick + 8,
  },
  {
    id: 'intel',
    highlight: 'intel',
    focus: nearestContact,
    done: (s) => s.inspectedContact !== null,
  },
  {
    id: 'attack',
    highlight: 'orders',
    focus: nearestContact,
    done: (s, b) =>
      newOrders(s.view, b).some((o) => o.kind === 'attack') ||
      s.view.battles.some((bt) =>
        bt.attackers.some((id) => s.view.units.some((u) => u.id === id && u.faction === s.view.faction)),
      ),
  },
  {
    id: 'hhour',
    highlight: 'hhour',
    done: (s, b) =>
      new Set(
        newOrders(s.view, b)
          .filter((o) => o.executeAt >= 0)
          .map((o) => o.unitId),
      ).size >= 2,
  },
  {
    id: 'posture',
    highlight: 'posture',
    done: (s, b) =>
      newOrders(s.view, b).some((o) => o.kind === 'posture') ||
      ownUnits(s.view).some((u) => b.postures[u.id] !== undefined && b.postures[u.id] !== u.posture),
  },
  {
    id: 'production',
    highlight: 'production',
    done: (s, b) => s.view.me.production.some((p) => !b.productionIds.includes(p.id)),
  },
  {
    id: 'research',
    highlight: 'research',
    done: (s, b) => s.view.me.research.current !== null && s.view.me.research.current !== b.research,
  },
  {
    id: 'supply',
    highlight: 'readout',
  },
  {
    id: 'victory',
    highlight: 'vp',
  },
];
